import type { QueryClient } from '@tanstack/react-query';
import { getSegmentAttentionGridRowKey } from 'app/utils/hilSegmentsAttentionUtils';
import type { ResolveEntity, ResolveResolution } from './types';

export type ParsedResolveStep = {
  order: number;
  action: string;
  label: string;
  required: boolean;
};

const HIL_SEGMENTS_ATTENTION_ENDPOINT =
  'nurturing-automation/automation-needing-attention';

function toTrimmedString(value: unknown): string {
  return value == null ? '' : String(value).trim();
}

function parseJsonIfString(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return value;
  }
}

function coerceUnknownArray(value: unknown): unknown[] | undefined {
  if (Array.isArray(value)) return value;
  const parsed = parseJsonIfString(value);
  return Array.isArray(parsed) ? parsed : undefined;
}

function pickResolutionRecord(row: Record<string, unknown>): Record<string, unknown> | null {
  const candidates: unknown[] = [parseJsonIfString(row.resolution)];
  const metadata = row.metadata;
  if (metadata && typeof metadata === 'object') {
    candidates.push(
      parseJsonIfString((metadata as Record<string, unknown>).resolution),
    );
  }

  for (const candidate of candidates) {
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      return candidate as Record<string, unknown>;
    }
  }
  return null;
}
export function extractEntitiesFromRow(row: unknown): ResolveEntity[] {
  if (!row || typeof row !== 'object') return [];
  const record = row as { entities?: unknown };
  const entities = record.entities;
  if (!Array.isArray(entities)) return [];
  return entities
    .filter(Boolean)
    .map((e): ResolveEntity => {
      if (!e || typeof e !== 'object') return {};
      const obj = e as Record<string, unknown>;
      const entity_type = toTrimmedString(obj.entity_type) || undefined;
      const entity_ids = Array.isArray(obj.entity_ids)
        ? (obj.entity_ids as unknown[])
            .map(id => toTrimmedString(id))
            .filter(Boolean)
        : undefined;
      const impacted_count = Number(obj.impacted_count);
      return {
        entity_type,
        entity_ids,
        impacted_count: Number.isFinite(impacted_count) ? impacted_count : undefined,
        metadata: obj.metadata,
      };
    })
    .filter((e) => Boolean(e.entity_type) || (e.entity_ids?.length ?? 0) > 0);
}

export function extractResolutionFromRow(row: unknown): ResolveResolution | null {
  if (!row || typeof row !== 'object') return null;
  const obj = pickResolutionRecord(row as Record<string, unknown>);
  if (!obj) return null;

  const summary = toTrimmedString(obj.summary) || undefined;
  const mode = toTrimmedString(obj.mode) || undefined;
  const can_bulk_reschedule =
    typeof obj.can_bulk_reschedule === 'boolean'
      ? obj.can_bulk_reschedule
      : undefined;
  const entity_actions = coerceUnknownArray(obj.entity_actions);
  const steps = coerceUnknownArray(obj.steps);
  return { summary, mode, can_bulk_reschedule, entity_actions, steps };
}

export function parseResolveSteps(
  resolution: ResolveResolution | null,
): ParsedResolveStep[] {
  const stepsArray = coerceUnknownArray(resolution?.steps) ?? [];
  if (stepsArray.length === 0) return [];

  return stepsArray
    .filter(Boolean)
    .map((rawStep, index): ParsedResolveStep => {
      const parsedStep = parseJsonIfString(rawStep);
      if (!parsedStep || typeof parsedStep !== 'object') {
        return {
          order: index + 1,
          action: '',
          label: '',
          required: false,
        };
      }

      const obj = parsedStep as Record<string, unknown>;
      const orderRaw = obj.order ?? obj.step_order ?? obj.sequence ?? index + 1;
      const order = Number(orderRaw);
      const action = toTrimmedString(
        obj.action ?? obj.step_action ?? obj.code ?? obj.action_code,
      );
      const label = toTrimmedString(
        obj.label ??
          obj.step_label ??
          obj.title ??
          obj.description ??
          obj.message ??
          obj.text,
      );
      const required =
        obj.required === true ||
        obj.is_required === true ||
        String(obj.required ?? '').toLowerCase() === 'true';

      return {
        order: Number.isFinite(order) ? order : index + 1,
        action,
        label,
        required,
      };
    })
    .filter((step) => Boolean(step.action || step.label))
    .sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return `${a.action}${a.label}`.localeCompare(`${b.action}${b.label}`);
    });
}

/** Prefer the latest cached automation-needing-attention row over stale grid state. */
export function findSegmentAttentionRowInQueryCache(
  queryClient: QueryClient,
  row: unknown,
): unknown {
  const rowKey = getSegmentAttentionGridRowKey(row);
  if (!rowKey) return row;

  const queries = queryClient.getQueriesData({ queryKey: ['GET'], exact: false });
  for (const [queryKey, queryData] of queries) {
    const keyText = JSON.stringify(queryKey);
    if (!keyText.includes(HIL_SEGMENTS_ATTENTION_ENDPOINT)) continue;

    const body =
      queryData && typeof queryData === 'object' && 'body' in queryData
        ? (queryData as { body?: { data?: unknown } }).body
        : undefined;
    const data = body?.data;
    if (!Array.isArray(data)) continue;

    const match = data.find(
      (candidate) => getSegmentAttentionGridRowKey(candidate) === rowKey,
    );
    if (match) return match;
  }

  return row;
}

export function resolveGridRowFromCellParams(cellParams: unknown): unknown {
  if (cellParams == null || typeof cellParams !== 'object') return cellParams;
  const record = cellParams as Record<string, unknown>;
  if ('row' in record && record.row != null) {
    return record.row;
  }
  return cellParams;
}

export function hasResolveEntitiesOrResolution(row: unknown): boolean {
  const entities = extractEntitiesFromRow(row);
  if (entities.length > 0) return true;
  const res = extractResolutionFromRow(row);
  return Boolean(res && (res.summary || res.mode || res.steps?.length || res.entity_actions?.length));
}

export function normalizeEntityType(value: unknown): string {
  return toTrimmedString(value).toLowerCase();
}

export function entityTypeLabel(entityType: string): string {
  const key = normalizeEntityType(entityType);
  if (key === 'dealer_value' || key === 'dealer values') return 'Dealer values';
  if (key === 'offer' || key === 'offers') return 'Offers';
  if (key === 'template' || key === 'templates') return 'Templates';
  if (key === 'template_intent') return 'Template intent';
  if (key === 'template_upload') return 'Template upload';
  if (key === 'dealer_special' || key === 'dealer_specials') return 'Dealer specials';
  if (key === 'footer_image' || key === 'footer_images') return 'Footer image';
  return entityType || 'Entity';
}

